import { useEffect, useState } from "react";
import { http, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, BookOpen, Star } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import RewardModal from "@/components/RewardModal";
import BadgeUnlockOverlay from "@/components/BadgeUnlockOverlay";

const disturbanceOpts = ["noise", "light", "pager", "kids", "caffeine", "phone", "pain"];

function localInputValue(offsetHours = 0) {
  const d = new Date(Date.now() + offsetHours * 3600 * 1000);
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function SleepLog({ onGoToBadges, onInviteReview }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({
    start: localInputValue(-9),
    end: localInputValue(-1),
    quality: 4,
    disturbances: [],
    fatigue_before: 4,
    fatigue_after: 2,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [reward, setReward] = useState({ open: false, entry: null });
  const [unlock, setUnlock] = useState({ badges: [], streak: null });

  const refresh = async () => {
    const [l, s] = await Promise.all([
      http.get("/sleep-logs").then((r) => r.data).catch(() => []),
      http.get("/sleep-stats").then((r) => r.data).catch(() => null),
    ]);
    setLogs(l); setStats(s);
  };

  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/sleep-logs", {
        ...form,
        start: new Date(form.start).toISOString(),
        end: new Date(form.end).toISOString(),
        quality: Number(form.quality),
        fatigue_before: Number(form.fatigue_before),
        fatigue_after: Number(form.fatigue_after),
      });
      const isGood = data.duration_hours >= 6 && data.quality >= 4;
      const newBadges = Array.isArray(data.newly_earned_badges) ? data.newly_earned_badges : [];
      if (newBadges.length > 0) {
        // Full-screen unlock takes precedence over the smaller reward modal.
        setUnlock({ badges: newBadges, streak: data.streak || null });
      } else if (isGood) {
        setReward({ open: true, entry: data });
      } else {
        toast.success("Sleep logged", { description: `${data.duration_hours}h · quality ${data.quality}/5` });
      }
      setForm({ ...form, notes: "" });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save log");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await http.delete(`/sleep-logs/${id}`); toast.success("Log removed"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to remove"); }
  };

  const toggle = (d) => {
    setForm((f) => ({
      ...f,
      disturbances: f.disturbances.includes(d) ? f.disturbances.filter((x) => x !== d) : [...f.disturbances, d],
    }));
  };

  const chartData = [...logs].reverse().map((l) => ({
    name: new Date(l.start).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    hours: l.duration_hours,
    quality: l.quality,
  }));

  return (
    <section className="space-y-6">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Sleep Log</h2>
        <p className="text-slate-400 text-sm mt-1">Track duration, quality, and disturbances. Feeds your circadian score.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <StatTile label="Avg sleep (14d)" value={`${stats?.avg_hours ?? 0}h`} />
        <StatTile label="Avg quality" value={`${stats?.avg_quality ?? 0}/5`} />
        <StatTile label="Sleep debt (7d)" value={`${stats?.sleep_debt ?? 0}h`} tint="text-amber-300" />
      </div>

      {chartData.length > 1 && (
        <div className="circa-card p-5">
          <div className="text-xs uppercase tracking-widest font-mono-ui text-slate-500 mb-3">Trend</div>
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="name" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0F141F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                <Line type="monotone" dataKey="hours" stroke="#818CF8" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="quality" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="circa-card p-6 grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Sleep start</Label>
          <Input type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="input-sleep-start" className="mt-1 bg-slate-900/60 border-white/10 text-slate-100" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Sleep end</Label>
          <Input type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="input-sleep-end" className="mt-1 bg-slate-900/60 border-white/10 text-slate-100" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Quality</Label>
          <Select value={String(form.quality)} onValueChange={(v) => setForm({ ...form, quality: v })}>
            <SelectTrigger data-testid="sleep-log-quality-rating" className="mt-1 bg-slate-900/60 border-white/10 text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F141F] border-white/10 text-slate-100">
              {[1,2,3,4,5].map((n) => <SelectItem key={n} value={String(n)}>{n} / 5</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Fatigue after</Label>
          <Select value={String(form.fatigue_after)} onValueChange={(v) => setForm({ ...form, fatigue_after: v })}>
            <SelectTrigger className="mt-1 bg-slate-900/60 border-white/10 text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F141F] border-white/10 text-slate-100">
              {[1,2,3,4,5].map((n) => <SelectItem key={n} value={String(n)}>{n} / 5</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-6">
          <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Disturbances</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {disturbanceOpts.map((d) => (
              <button
                type="button"
                key={d}
                onClick={() => toggle(d)}
                data-testid={`disturbance-${d}`}
                className={`pill ${form.disturbances.includes(d) ? "bg-rose-950/70 border-rose-500/40 text-rose-200" : "bg-slate-900/60 border-white/10 text-slate-400"} hover:border-rose-400/50`}
              >{d}</button>
            ))}
          </div>
        </div>

        <div className="md:col-span-6 flex justify-end">
          <Button type="submit" data-testid="btn-submit-sleep-log" disabled={busy} className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo">
            <Plus className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Log sleep"}
          </Button>
        </div>
      </form>

      <div className="circa-card p-6">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-4 h-4 text-indigo-300" />
          <div className="font-display text-lg font-semibold">History</div>
        </div>
        <div className="divide-y divide-white/5">
          {logs.length === 0 && <div className="text-center text-slate-500 py-8 text-sm">No entries yet.</div>}
          {logs.map((l) => (
            <div key={l.id} className="py-3 flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-100">{l.duration_hours}h</span>
                  <span className="flex items-center gap-0.5 text-amber-300 text-xs">
                    {Array.from({ length: l.quality }).map((_, i) => <Star key={i} className="w-3 h-3 fill-current" />)}
                  </span>
                  {l.disturbances?.length > 0 && (
                    <span className="text-xs text-rose-300 font-mono-ui">· {l.disturbances.join(", ")}</span>
                  )}
                </div>
                <div className="text-xs text-slate-400 font-mono-ui mt-1">{new Date(l.start).toLocaleString()} → {new Date(l.end).toLocaleString()}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(l.id)} data-testid={`btn-delete-log-${l.id}`} className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 rounded-full">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <RewardModal
        open={reward.open}
        onOpenChange={(v) => setReward({ open: v, entry: v ? reward.entry : null })}
        sleepEntry={reward.entry}
      />

      <BadgeUnlockOverlay
        badges={unlock.badges}
        streak={unlock.streak}
        onClose={() => setUnlock({ badges: [], streak: null })}
        onGoToBadges={onGoToBadges}
        onInviteReview={onInviteReview}
      />
    </section>
  );
}

function StatTile({ label, value, tint = "text-indigo-300" }) {
  return (
    <div className="circa-card p-5">
      <div className="text-xs uppercase tracking-widest font-mono-ui text-slate-500 mb-2">{label}</div>
      <div className={`font-display text-3xl font-bold ${tint}`}>{value}</div>
    </div>
  );
}
